// Libs
import classNames from 'classnames/bind';
import { Box, Button, InputLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
// Others
import { IFormNoteSchedule, INoteSchedule, IParamNoteSchedule, ISchedule } from '~/utils/interface/schedule';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  COMMA_SEPARATOR,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { formatDate, getFullName } from '~/utils/helper/common';
import { DateFormatEnum, ParamsBusinessTypeEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createNoteSchedule, getNoteScheduleList } from '~/thunks/notes/notesThunk';
import { selectIsRefreshNoteSchedule } from '~/thunks/notes/notesSlice';
// Styles, images, icons
import styles from './ScheduleDetailNotes.module.scss';

type Props = {
  data?: ISchedule;
};

const cx = classNames.bind(styles);

export const schema = (t: TFunction) => {
  return yup
    .object()
    .shape({
      content: yup.string().required(t('common_validate_field_required', { ns: 'common/base' })),
    })
    .required();
};

export const defaultValuesForm: IFormNoteSchedule = {
  content: EMPTY_STRING,
};

const ScheduleDetailNotes = (props: Props) => {
  //#region Destructuring Props
  const { data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormNoteSchedule>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const isRefreshNoteScheduleList = useSelector(selectIsRefreshNoteSchedule);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notesList, setNotesList] = useState<INoteSchedule[]>([]);
  const [paramObject] = useState<IParamNoteSchedule>({
    page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_1000,
    businessType: ParamsBusinessTypeEnum.SCHEDULE,
  });
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!data?.id) return;
    const newParamObject: IParamNoteSchedule = { ...paramObject, businessId: data.id };
    setIsLoading(true);
    handleGetNoteScheduleList(newParamObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isRefreshNoteScheduleList || !data?.id) return;
    const newParamObject: IParamNoteSchedule = { ...paramObject, businessId: data.id };
    handleGetNoteScheduleList(newParamObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshNoteScheduleList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetNoteScheduleList = (params: IParamNoteSchedule) => {
    dispatch(getNoteScheduleList(params))
      .unwrap()
      .then((resp) => {
        const { responses } = resp.data;
        if (!responses) return;

        setNotesList(responses);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitForm = (dataNote: IFormNoteSchedule) => {
    const newData = {
      ...dataNote,
      businessId: data?.id || EMPTY_STRING,
      businessType: ParamsBusinessTypeEnum.SCHEDULE,
    };

    setIsLoading(true);

    dispatch(createNoteSchedule(newData))
      .unwrap()
      .then((res) => {
        reset(defaultValuesForm);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderNoteItem = (note: INoteSchedule) => {
    return (
      <>
        <Typography className={cx('label')} />
        <Typography className={cx('noteContent')}>
          {`"${note.content || EMPTY_STRING}"${COMMA_SEPARATOR}`}
          <Typography className={cx('noteContent')}>
            {` ${t('schedule_note_detail_created_by_label')} ${
              getFullName({ firstName: note.firstName, lastName: note.lastName }) || EMPTY_STRING
            }${COMMA_SEPARATOR} ${
              note?.createdAt ? formatDate(note?.createdAt, DateFormatEnum.MM_DD_YYYY_HH_AM_PM) : EMPTY_STRING
            }`}
          </Typography>
        </Typography>
      </>
    );
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='scheduleDetailNotes' className={cx('container')}>
      <Box component={'form'} className={cx('form')}>
        <Typography className={cx('title')}>{t('schedule_note_detail_title')}</Typography>

        <Box component={'div'} className={cx('contentNote')}>
          {notesList.length > DEFAULT_NUMBER_ZERO &&
            notesList.map((note, index) => (
              <Box key={index} className={cx('noteItem')}>
                {renderNoteItem(note)}
              </Box>
            ))}
        </Box>

        <Box className={cx('input')}>
          <InputLabel required className={cx('labelInput')}>
            {t('schedule_note_detail_notes_label')}
          </InputLabel>
          <Controller
            name='content'
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput value={value} onChange={onChange} errorMessage={errors?.content?.message} />
            )}
          />
        </Box>

        <Box className={cx('button')}>
          <Button variant='contained' onClick={handleSubmit(handleSubmitForm)}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Box>
      </Box>
      {isLoading && <Loading />}
    </Box>
  );
};

export default ScheduleDetailNotes;
